import React, { useContext, useEffect, useState } from 'react';

import DisplayItem from './field_inputs/item/DisplayItem';
import AddDeal from '../AddDeal';
import PipelineSummarySlim from '../deal/PipelineSummarySlim';
import { DeepCopy } from '../../tables/shared/TableEventHelpers';
import { DataContext as SharedContext } from '../../../stores/crm-store';

/*
 *
 *   COMPONENTS
 *
 */

export const DealsSummary = ({ item, deals }) => {
    const [openDeals, setOpenDeals] = useState([]);
    const [closedDeals, setClosedDeals] = useState([]);

    useEffect(() => {
        setOpenDeals(deals.filter(elem => elem.dealStatus === null));
        setClosedDeals(deals.filter(elem => elem.dealStatus !== null));
    }, [deals]);

    if (deals === null || deals.length === 0)
        return null;

    return (
        <div className='d-flex flex-column card w-100 detail-card-info-form p-3'>
            <div className='d-flex flex-direction-row justify-content-between pb-2'>
                <h3 className='crm-header c-purple'>Deals</h3>
                <AddDeal initialValues={{ customer: item, name: `${item.name} deal` }} />
            </div>
            <div className='crm-deals-hline mb-2' />
            <h4 className='crm-header pb-2 font-weight-bold'>Open Deals ({openDeals.length})</h4>
            <DealCards openDeals={openDeals} />
            <h4 className='crm-header pb-2 font-weight-bold'>Closed Deals ({closedDeals.length})</h4>
            <WinLossSummary closedDeals={closedDeals} />
            
        </div>
    );
};

const DealCards = ({ openDeals }) => {
    const [firstThree, setFirstThree] = useState([]);

    useEffect(() => {
        setFirstThree(openDeals.slice(0,3));
    }, [openDeals]);

    if (openDeals === null || openDeals.length === 0)
        return null;

    return (
        <div className='d-flex flex-column pb-2'>
            {firstThree.map((elem) => { return <DealCard key={`deal-card-${elem.id}`} deal={elem} />; })}
        </div>
    );
};

const DealCard = ({ deal }) => {
    const [pipelineStages, setPipelineStages] = useState([]);
    const {
        store: { pipelineOptions },
    } = useContext(SharedContext);

    useEffect(() => {
        const pipeline = pipelineOptions.find((elem) => elem.id === '00000001-0000-0000-0000-000000000000');
        setPipelineStages(DeepCopy(pipeline.pipelineStages));
    }, [pipelineOptions]);

    const dealUrl = '/crmDealDetails/' + deal.id;
    return (
        <div className='card d-flex flex-column p-2 ellipsis-text'>
            <p className='font-weight-bold mb-1'>
                <DisplayItem {...{ item: { name: deal.name, link: dealUrl } }} />
            </p>
            <div className='d-flex flex-row'>
                <p className='mb-0'>SEK {deal.value}</p>
            </div>
            <PipelineSummarySlim value={deal.pipelineStageId} stages={pipelineStages} />
        </div>
    );
};

const WinLossSummary = ({ closedDeals }) => {
    const [wonDeals, setWonDeals] = useState([]);
    const [lostDeals, setLostDeals] = useState([]);
    const [winBarCN, setWinBarCN] = useState('');
    const [lossBarCN, setLossBarCN] = useState('');
    const [winPercentage, setWinPercentage] = useState(0.0);
    const [lossPercentage, setLossPercentage] = useState(0.0);

    useEffect(() => {
        const wDeals = closedDeals.filter(elem => elem.dealStatus === true);
        const lDeals = closedDeals.filter(elem => elem.dealStatus === false);
        setWonDeals(wDeals);
        setLostDeals(lDeals);
        setWinPercentage((wDeals.length/closedDeals.length)*100.0);
        setLossPercentage((lDeals.length/closedDeals.length)*100.0);
        setWinBarCN(
            (wDeals.length > 0 ? ' left-bar-rounded' : '') + 
            (wDeals.length === closedDeals.length ? ' right-bar-rounded' : '')
        );
        setLossBarCN(
            (lDeals.length > 0 ? ' right-bar-rounded' : '') +
            (lDeals.length === closedDeals.length ? ' left-bar-rounded' : '')
        );
    }, [closedDeals]);

    if (closedDeals === null || closedDeals.length === 0)
        return null;
    
    return (
        <div className='d-flex flex-column'>
            <WinLossBar winBarCN={winBarCN} lossBarCN={lossBarCN} winPercentage={winPercentage} lossPercentage={lossPercentage} />
            <ClosedDealsSummary title='Won' resClass='c-green' actDeals={wonDeals} percentage={winPercentage} />
            <ClosedDealsSummary title='Lost' resClass='c-red' actDeals={lostDeals} percentage={lossPercentage} />
        </div>
    );
};

const WinLossBar = ({ winBarCN, lossBarCN, winPercentage, lossPercentage }) => {
    return (
        <div className='win-loss-bar w-100 d-flex flex-direction-row pb-2'>
            <div className={`h-100 bg-light-green${winBarCN}`} style={{ width: `${winPercentage}%` }}/>
            <div className={`h-100 bg-red${lossBarCN}`} style={{ width: `${lossPercentage}%` }} />
        </div>
    );
};

const ClosedDealsSummary = ({ title, resClass, actDeals, percentage }) => {
    return (
        <div className='win-loss-summary w-100 d-flex flex-direction-row justify-content-between pb-1'>
            <p className={resClass} style={{ width: '15%' }}>{title}</p>
            <p className='d-flex justify-content-center' style={{ width: '15%' }}>{actDeals.length}</p>
            <p className='d-flex justify-content-end' style={{ width: '20%' }}>{percentage}%</p>
            <p className='d-flex justify-content-end' style={{ width: '50%' }}>
                SEK {calculateValue(actDeals)}
            </p>
        </div>
    );
};

/*
 *
 *   FUNCTIONS
 *
 */

const calculateValue = (deals) => {
    let res = 0;
    deals.forEach((elem) => {
        res += elem.value !== null ? elem.value : 0;
    });

    let origString = res.toString();
    let resString = res.toString();
    for (let i=resString.length; i>=0; i--) {
        if (i !== 0 && i !== resString.length && (origString.length - i)%3 === 0 ) {
            resString = resString.substring(0, i) + ',' + resString.substring(i);
            i--;
        }
    }

    return resString;
};